/**
 * Navigation utility functions
 */
import { setWorkOrderReturn, type WorkOrderReturn } from './workOrderReturn';

export type AppView =
  | 'dashboard'
  | 'dashboard:accounts'
  | 'dashboard:location'
  | 'workorders'
  | 'customers'
  | 'reports'
  | 'profile'
  | 'admin'
  | 'inventory'
  | 'mywork'
  | 'help';

export const navigateToWorkOrders = (
  accountId: string | undefined,
  locationId: string,
  options?: { tagId?: string; departmentId?: string; status?: string[]; create?: boolean; pastDue?: boolean }
) => {
  if (!accountId) {
    console.warn('Cannot navigate to work orders without account ID');
    return;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'workorders');
    params.set('acc', accountId);
    params.set('co', locationId);

    // Clear work order detail parameter to ensure we show the list view
    params.delete('wo');

    // Remove sort parameters to let the component use its default sort order (priority desc)
    params.delete('sort');
    params.delete('dir');

    // Add create parameter if specified
    if (options?.create) {
      params.set('create', 'true');
    } else {
      params.delete('create');
    }

    if (options?.tagId) {
      params.set('tag_ids', options.tagId);
    } else {
      params.delete('tag_ids');
    }

    if (options?.departmentId) {
      params.set('dept', options.departmentId);
    } else {
      params.delete('dept');
    }

    if (options?.status && options.status.length > 0) {
      params.set('status', options.status.join(','));
    } else {
      params.delete('status');
    }

    // Only the work past due in its department (the dashboards' past-due rule)
    if (options?.pastDue) {
      params.set('past_due', '1');
    } else {
      params.delete('past_due');
    }

    const state = {
      view: 'workorders',
      account: accountId,
      location: locationId,
      ...(options?.create && { create: true }),
      ...(options?.tagId && { tag_ids: options.tagId }),
      ...(options?.departmentId && { department: options.departmentId }),
      ...(options?.status && options.status.length > 0 && { status: options.status.join(',') }),
      ...(options?.pastDue && { past_due: true }),
    };

    window.history.pushState(state, '', `${window.location.pathname}?${params.toString()}`);
    // Trigger navigation
    window.dispatchEvent(new CustomEvent('navigate', { detail: state }));
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

// Creation-intent params. Any navigation that isn't explicitly opening the
// create modal must drop these: useWorkOrderUrlSync re-opens the creation
// modal whenever `create=true` is in the URL, so letting it ride along opens
// the wrong dialog (e.g. the created-work-order toast's "Open" action).
// Exported so App-level navigation (openSearchSelection, handleNavigate)
// applies the same hygiene.
export const clearCreateParams = (params: URLSearchParams) => {
  params.delete('create');
  params.delete('prefill_vin');
  params.delete('prefill_stock');
  params.delete('prefill_year');
  params.delete('prefill_make');
  params.delete('prefill_model');
  params.delete('prefill_color');
  params.delete('prefill_mileage');
};

/** URL flag: open the work order on its PDR estimate ("Start PDR estimate"). */
const PDR_PARAM = 'pdr';

/**
 * True once if the URL asks to open the PDR estimate; removes the flag so a
 * reload or back-navigation does not reopen it.
 */
export const consumePdrDeepLink = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get(PDR_PARAM) !== 'start') return false;
    params.delete(PDR_PARAM);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}`);
    return true;
  } catch {
    return false;
  }
};

export const navigateToWorkOrder = (
  workOrderId: string,
  opts: {
    openPdrEstimate?: boolean;
    /** The work order's own account and location, when the page shows another (e.g. the executive dashboard). */
    accountId?: string;
    locationId?: string;
    /** Where closing the work order goes back to; none = its store's dashboard. */
    returnTo?: WorkOrderReturn;
  } = {}
) => {
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'workorders');
    params.set('wo', workOrderId);
    // Another account or location: the previous list's filters belong to
    // that scope, not this one. Within the same scope they stay.
    const scopeChanges =
      (opts.accountId && opts.accountId !== params.get('acc')) || (opts.locationId && opts.locationId !== params.get('co'));
    if (scopeChanges) {
      for (const key of ['dept', 'status', 'tag_ids', 'past_due', 'page']) params.delete(key);
    }
    if (opts.accountId) params.set('acc', opts.accountId);
    if (opts.locationId) params.set('co', opts.locationId);
    if (opts.openPdrEstimate) params.set(PDR_PARAM, 'start');
    else params.delete(PDR_PARAM);
    // The page opening it decides where closing goes back to.
    setWorkOrderReturn(params, opts.returnTo, workOrderId);
    clearCreateParams(params);
    window.history.pushState(
      {
        view: 'workorders',
        wo: workOrderId,
        ...(opts.accountId && { account: opts.accountId }),
        ...(opts.locationId && { location: opts.locationId }),
      },
      '',
      `${window.location.pathname}?${params.toString()}`
    );
    // Trigger navigation
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: {
          view: 'workorders',
          wo: workOrderId,
          ...(opts.accountId && { account: opts.accountId }),
          ...(opts.locationId && { location: opts.locationId }),
        },
      })
    );
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/**
 * Navigate to a work order with a specific note highlighted — the same URL
 * shape the notification-email deep links use (?wo=&noteId=&serviceId=),
 * which useWorkOrderUrlSync turns into highlightNoteId/highlightServiceId
 * and WorkOrderDetail then opens the note from. Used by the My Work
 * Mentions bar.
 */
export const navigateToWorkOrderNote = (workOrderId: string, noteId?: string, serviceId?: string) => {
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'workorders');
    params.set('wo', workOrderId);
    if (noteId) params.set('noteId', noteId);
    else params.delete('noteId');
    if (serviceId) params.set('serviceId', serviceId);
    else params.delete('serviceId');
    clearCreateParams(params);
    window.history.pushState({ view: 'workorders', wo: workOrderId }, '', `${window.location.pathname}?${params.toString()}`);
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'workorders', wo: workOrderId },
      })
    );
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/**
 * Navigate to an invoice batch. Batches have no view of their own — the URL
 * opens the Reports view on its invoices tab, where ReportsDashboard reads the
 * `batch` param on mount and popstate and opens BatchDetailModal. Same shape
 * the global search uses when an operator picks a batch.
 */
export const navigateToInvoiceBatch = (batchId: string) => {
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'reports');
    params.set('tab', 'invoices');
    params.set('batch', batchId);
    params.delete('wo');
    clearCreateParams(params);
    window.history.pushState({ view: 'reports' }, '', `${window.location.pathname}?${params.toString()}`);
    // popstate, not the `navigate` CustomEvent: Reports only listens for
    // popstate to consume `batch`, and App's popstate handler picks the view
    // out of the URL without clearing the account/location params.
    window.dispatchEvent(new PopStateEvent('popstate'));
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

export const navigateToLocationDashboard = (accountId: string | undefined, locationId: string, options?: { departmentId?: string }) => {
  if (!accountId) {
    console.warn('Cannot navigate to location dashboard without account ID');
    return;
  }

  try {
    const state = {
      view: 'dashboard:location',
      account: accountId,
      location: locationId,
      ...(options?.departmentId && { highlightDepartment: options.departmentId }),
    };

    window.history.pushState(state, '', locationDashboardHref(accountId, locationId, options));
    // Trigger navigation
    window.dispatchEvent(new CustomEvent('navigate', { detail: state }));
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/**
 * The address of a location's dashboard, built from the current one the same
 * way navigateToLocationDashboard navigates — so a back arrow opened in a new
 * tab lands exactly where a plain click goes.
 */
export const locationDashboardHref = (
  accountId: string,
  locationId: string,
  options?: { departmentId?: string },
  search: string = window.location.search
): string => {
  const params = new URLSearchParams(search);
  params.set('view', 'dashboard:location');
  params.set('acc', accountId);
  params.set('co', locationId);
  // The Accounts page's place belongs to that page (a group of another account would leak).
  params.delete(ACCOUNTS_SCOPE_PARAM);
  params.delete(ACCOUNTS_GROUP_PARAM);
  // A work order's return place belongs to that work order.
  setWorkOrderReturn(params, null);
  clearCreateParams(params);

  if (options?.departmentId) {
    params.set('highlight_dept', options.departmentId);
  } else {
    params.delete('highlight_dept');
  }
  return `${window.location.pathname}?${params.toString()}`;
};

export const navigateToInventory = (accountId: string | undefined, locationId: string) => {
  try {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'inventory');
    if (accountId) params.set('acc', accountId);
    params.set('co', locationId);

    const state = {
      view: 'inventory',
      account: accountId,
      location: locationId,
    };

    window.history.pushState(state, '', `${window.location.pathname}?${params.toString()}`);
    window.dispatchEvent(new CustomEvent('navigate', { detail: state }));
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/**
 * Where the Accounts page is, held in the address so Back, a reload and a
 * shared link land in the same place:
 *
 *   ?view=dashboard:accounts&scope=all                    every account
 *   ?view=dashboard:accounts&scope=account&acc=…          one account
 *   …&grp=<group id>                                      one of its location groups
 *   …&co=<location id>                                    the location last opened (highlighted)
 *
 * Without `scope` the page shows its default: every account for a system
 * admin, the session's account for everyone else.
 */
export const ACCOUNTS_SCOPE_PARAM = 'scope';
export const ACCOUNTS_GROUP_PARAM = 'grp';

export interface AccountsPlace {
  scope?: 'all' | 'account';
  accountId?: string;
  groupId?: string;
  /** The location to highlight (the one last opened from here). */
  locationId?: string;
}

/** The Accounts page's place in an address. */
export const readAccountsPlace = (search: string = window.location.search): AccountsPlace => {
  const params = new URLSearchParams(search);
  const raw = params.get(ACCOUNTS_SCOPE_PARAM);
  const scope = raw === 'all' || raw === 'account' ? raw : undefined;
  const accountId = params.get('acc') || undefined;
  return {
    scope,
    accountId,
    // A group belongs to one account's page: ignored anywhere else (the same rule as accountsPlaceHref).
    groupId: scope === 'account' && accountId ? params.get(ACCOUNTS_GROUP_PARAM) || undefined : undefined,
    locationId: params.get('co') || undefined,
  };
};

/** The address of an Accounts page place, built from the current one (other views' params ride along). */
export const accountsPlaceHref = (place: AccountsPlace, search: string = window.location.search): string => {
  const params = new URLSearchParams(search);
  params.set('view', 'dashboard:accounts');
  if (place.scope) params.set(ACCOUNTS_SCOPE_PARAM, place.scope);
  else params.delete(ACCOUNTS_SCOPE_PARAM);
  if (place.accountId) params.set('acc', place.accountId);
  if (place.scope === 'account' && place.groupId) params.set(ACCOUNTS_GROUP_PARAM, place.groupId);
  else params.delete(ACCOUNTS_GROUP_PARAM);
  if (place.locationId) params.set('co', place.locationId);
  else params.delete('co');
  clearCreateParams(params);
  params.delete('wo');
  return `${window.location.pathname}?${params.toString()}`;
};

const goToAccountsPlace = (place: AccountsPlace, replace = false) => {
  try {
    const href = accountsPlaceHref(place);
    const state = {
      view: 'dashboard:accounts',
      ...(place.accountId && { account: place.accountId }),
      ...(place.locationId && { location: place.locationId }),
    };
    if (replace) window.history.replaceState(state, '', href);
    else window.history.pushState(state, '', href);
    window.dispatchEvent(new CustomEvent('navigate', { detail: state }));
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/**
 * Record a place the Accounts page moved to by itself (another account, a
 * group, back to every account) without telling the app to change view: a
 * new history entry, so Back returns to the place before.
 */
export const recordAccountsPlace = (place: AccountsPlace, opts: { replace?: boolean } = {}) => {
  try {
    const href = accountsPlaceHref(place);
    if (`${window.location.pathname}${window.location.search}` === href) return;
    const state = { view: 'dashboard:accounts', ...(place.accountId && { account: place.accountId }) };
    if (opts.replace) window.history.replaceState(state, '', href);
    else window.history.pushState(state, '', href);
  } catch (error) {
    console.error('Navigation error:', error);
  }
};

/** The All accounts page. */
export const allAccountsHref = (search: string = window.location.search): string => accountsPlaceHref({ scope: 'all' }, search);
export const navigateToAllAccounts = () => goToAccountsPlace({ scope: 'all' });

/**
 * The address of one account's dashboard (every location), built from the
 * current one the same way navigateToAccountDashboard navigates — so a link
 * opened in a new tab lands exactly where a plain click goes.
 */
export const accountDashboardHref = (
  accountId?: string | undefined,
  locationId?: string | undefined,
  groupId?: string,
  search: string = window.location.search
): string => accountsPlaceHref({ scope: accountId ? 'account' : undefined, accountId, locationId, groupId }, search);

export const navigateToAccountDashboard = (accountId?: string | undefined, locationId?: string | undefined, groupId?: string) =>
  goToAccountsPlace({ scope: accountId ? 'account' : undefined, accountId, locationId, groupId });

/**
 * A top-level move (the main navigation, the logo) starts its view clean: the
 * Accounts page's scope and group never ride along, and the location only
 * where it means something (the location dashboard, work orders).
 */
export const clearPlaceParams = (params: URLSearchParams, view: string): void => {
  params.delete(ACCOUNTS_SCOPE_PARAM);
  params.delete(ACCOUNTS_GROUP_PARAM);
  if (view !== 'dashboard:location' && view !== 'workorders') params.delete('co');
};

export const navigateToView = (view: AppView, params?: Record<string, string>) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('view', view);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        urlParams.set(key, value);
      });
    }

    window.history.pushState(
      {
        view,
        ...params,
      },
      '',
      `${window.location.pathname}?${urlParams.toString()}`
    );

    // Trigger navigation
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view, ...params },
      })
    );
  } catch (error) {
    console.error('Navigation error:', error);
  }
};
